import React, { useState, useEffect, useRef, useCallback } from 'react';
import { chat } from '../../api';
import './ChatRoom.css';

const REFRESH_MS = 2 * 1000; // while a chat is open, look for new messages and read receipts this often

const timeLabel = (d) => {
  const date = new Date(d);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const isCoachRole = (role) => role === 'coach' || role === 'headcoach';

// One private conversation with one person
const Conversation = ({ person, onBack }) => {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [fresh, setFresh] = useState(() => new Set()); // ids that just arrived: they slide in
  const [flipped, setFlipped] = useState(() => new Set()); // my messages that just turned from Sent to Seen
  const listRef = useRef(null);
  const stickToBottom = useRef(true);
  const lastId = useRef('');
  const prevRef = useRef(null); // the previous batch, so only genuinely new things animate (not the first load)
  const firstScroll = useRef(true);

  // Mark ids for a moment so their CSS animation plays once
  const flash = useCallback((setter, ids) => {
    if (ids.length === 0) return;
    setter(prev => new Set([...prev, ...ids]));
    setTimeout(() => setter(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; }), 900);
  }, []);

  const load = useCallback(() => {
    return chat.getWith(person._id)
      .then(res => {
        const incoming = res.data.messages || [];
        const prev = prevRef.current;
        if (prev) {
          const before = new Map(prev.map(m => [m._id, m]));
          flash(setFresh, incoming.filter(m => !before.has(m._id)).map(m => m._id));
          flash(setFlipped, incoming.filter(m => m.mine && m.readAt && before.has(m._id) && !before.get(m._id).readAt).map(m => m._id));
        }
        prevRef.current = incoming;
        // Don't re-render when nothing changed (this runs every 2 seconds)
        setMessages(old => {
          if (old && old.length === incoming.length && old.every((m, i) => m._id === incoming[i]._id && !!m.readAt === !!incoming[i].readAt)) return old;
          return incoming;
        });
      })
      .catch(err => {
        setError(err?.response?.data?.error || 'Could not load this chat');
        setMessages(prev => prev || []);
      });
  }, [person._id, flash]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Follow the newest message, unless the reader has scrolled up to look at older ones. The first view jumps; later ones glide.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const newest = messages[messages.length - 1]._id;
    if (newest !== lastId.current) {
      lastId.current = newest;
      const el = listRef.current;
      if (el && stickToBottom.current) {
        if (firstScroll.current) el.scrollTop = el.scrollHeight;
        else el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
      firstScroll.current = false;
    }
  }, [messages]);

  const onScroll = () => {
    const el = listRef.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await chat.send(person._id, body);
      stickToBottom.current = true;
      flash(setFresh, [res.data.message._id]);
      prevRef.current = [...(prevRef.current || []), res.data.message];
      setMessages(prev => [...(prev || []), res.data.message]);
      setText('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send the message');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await chat.remove(m._id);
      setMessages(prev => (prev || []).filter(x => x._id !== m._id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete the message');
    }
  };

  const onKeyDown = (e) => {
    // Enter sends on a computer; on a phone keyboard Enter is a new line
    if (e.key === 'Enter' && !e.shiftKey && window.matchMedia && window.matchMedia('(hover: hover)').matches) handleSend(e);
  };

  return (
    <div className="chat-room">
      <div className="chat-header">
        <button type="button" className="chat-back" onClick={onBack} aria-label="Back to all chats">← Chats</button>
        <h4 className="chat-title">
          {person.name}
          {isCoachRole(person.role) && <span className="chat-role">Coach</span>}
          {person.role === 'manager' && <span className="chat-role">Manager</span>}
        </h4>
      </div>

      <div className="chat-list" ref={listRef} onScroll={onScroll}>
        {messages === null ? (
          <div className="chat-empty">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">No messages with {person.name} yet. Only the two of you can see this chat.</div>
        ) : messages.map(m => (
          <div key={m._id} className={`chat-row ${m.mine ? 'mine' : 'theirs'}${fresh.has(m._id) ? ' chat-new' : ''}`}>
            <div className="chat-bubble">{m.text}</div>
            <div className="chat-time">
              <span>{timeLabel(m.createdAt)}</span>
              {m.mine && (
                <span key={m.readAt ? 'seen' : 'sent'} className={`chat-receipt${m.readAt ? ' seen' : ''}${flipped.has(m._id) ? ' chat-receipt-flip' : ''}`}>
                  {m.readAt ? '✓✓ Seen' : '✓ Sent'}
                </span>
              )}
              {m.mine && <button type="button" className="chat-del" onClick={() => handleDelete(m)}>Delete</button>}
            </div>
          </div>
        ))}
      </div>

      <form className="chat-form" onSubmit={handleSend}>
        <textarea
          className="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Message ${person.name}...`}
          maxLength={2000}
          rows={2}
        />
        <button type="submit" className="submit-button" disabled={sending || !text.trim()} style={{ padding: '10px 16px' }}>
          {sending ? '...' : 'Send'}
        </button>
      </form>
      {error && <p className="chat-error">{error}</p>}
    </div>
  );
};

// Everyone you can chat with; tap one to open a private conversation
const Chat = () => {
  const [contacts, setContacts] = useState(null);
  const [open, setOpen] = useState(null); // the person whose conversation is showing
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const loadContacts = useCallback(() => {
    return chat.getContacts()
      .then(res => { setContacts(res.data.contacts || []); setError(''); })
      .catch(() => { setContacts(prev => prev || []); setError('Could not load your chats'); });
  }, []);

  useEffect(() => {
    loadContacts();
    const timer = setInterval(() => { if (!document.hidden) loadContacts(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadContacts]);

  // A message notification was tapped: open that person's conversation
  useEffect(() => {
    const openFromTap = () => {
      let id = '';
      try { id = sessionStorage.getItem('focusChatWith') || ''; sessionStorage.removeItem('focusChatWith'); } catch (e) { /* ignore */ }
      if (!id) return;
      chat.getContacts().then(res => {
        const list = res.data.contacts || [];
        setContacts(list);
        const person = list.find(c => String(c._id) === String(id));
        if (person) setOpen(person);
      }).catch(() => {});
    };
    openFromTap();
    window.addEventListener('focusChatWith', openFromTap);
    return () => window.removeEventListener('focusChatWith', openFromTap);
  }, []);

  if (open) {
    return <Conversation person={open} onBack={() => { setOpen(null); loadContacts(); }} />;
  }

  const q = query.trim().toLowerCase();
  const shown = (contacts || []).filter(c => !q || c.name.toLowerCase().includes(q));
  const coaches = shown.filter(c => isCoachRole(c.role));
  const managers = shown.filter(c => c.role === 'manager');
  const people = shown.filter(c => !isCoachRole(c.role) && c.role !== 'manager');

  const row = (c) => (
    <button key={c._id} type="button" className="chat-contact" onClick={() => setOpen(c)}>
      <span className="chat-avatar">{(c.name || '?').trim().charAt(0).toUpperCase()}</span>
      <span className="chat-contact-main">
        <span className="chat-contact-name">
          {c.name}{isCoachRole(c.role) && <span className="chat-role">Coach</span>}{c.role === 'manager' && <span className="chat-role">Manager</span>}
        </span>
        <span className="chat-contact-last">
          {c.lastText ? `${c.lastMine ? 'You: ' : ''}${c.lastText}` : 'No messages yet'}
        </span>
      </span>
      {c.unread > 0 && <span className="chat-unread">{c.unread}</span>}
    </button>
  );

  return (
    <div className="chat-room">
      <div className="chat-header">
        <h4 className="chat-title">💬 Chats</h4>
        <span className="chat-sub">Private, one to one</span>
      </div>

      {contacts && contacts.length > 6 && (
        <input
          className="chat-input chat-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name"
        />
      )}

      <div className="chat-contacts">
        {error && <p className="chat-error">{error}</p>}
        {contacts === null ? (
          <div className="chat-empty">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="chat-empty">There is nobody to chat with yet. Once your coach adds you, they and the other players will show up here.</div>
        ) : shown.length === 0 ? (
          <div className="chat-empty">Nobody matches "{query}".</div>
        ) : (
          <>
            {coaches.length > 0 && <div className="chat-group">Coach</div>}
            {coaches.map(row)}
            {managers.length > 0 && <div className="chat-group">Managers</div>}
            {managers.map(row)}
            {people.length > 0 && <div className="chat-group">{coaches.length > 0 ? 'Players' : 'Students'}</div>}
            {people.map(row)}
          </>
        )}
      </div>
    </div>
  );
};

export default Chat;
